import React, { useState, useMemo } from 'react';
import {
  Table,
  Flex,
  Typography,
  Button,
  Tag,
  Pagination,
  Tooltip,
  message,
} from 'antd';
import {
  ExpandAltOutlined,
  ShrinkOutlined,
  DownloadOutlined,
  DollarOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { formatCurrency, formatDateMMDDYYYY } from '../../../utils/helpers';
import { loftColors, loftFontWeights } from '../../../antd/theme';
import {
  HistoryTableRow,
  GroupedHistory,
  getFinancialsHistoryStatusStyles,
  getEffectiveStatus,
  getInvoiceDisplayIdsForPayNowTracking,
  safeParseAmount,
  ROWS_PER_PAGE,
  handleDownloadInvoice,
} from './Financials.utils';
import type { IntacctCreditMemo } from '../../../schema/generated/schema';

const { Text } = Typography;

/** Matches other table cells; raw strings inherit Ant Design default (~14px) otherwise. */
const CELL_FS = 12;

// ============================================================================
// Status Badge Component
// ============================================================================

const HistoryStatusBadge: React.FC<{
  row: HistoryTableRow;
  status: string;
}> = ({ row, status }) => {
  const styles = getFinancialsHistoryStatusStyles(row);

  return (
    <Tag
      style={{
        backgroundColor: styles.backgroundColor,
        borderColor: styles.borderColor,
        color: styles.textColor,
        fontSize: CELL_FS,
        fontWeight: loftFontWeights.semibold,
        borderRadius: 999,
        padding: '2px 12px',
        lineHeight: '20px',
      }}
    >
      {status}
    </Tag>
  );
};

// ============================================================================
// Main History View Component
// ============================================================================

interface FinancialsHistoryViewProps {
  billingAccountId: string;
  rows: HistoryTableRow[];
  totalRowCount: number;
  emptyMessage: string;
  currentPage: number;
  totalPages: number;
  selectedRowKeys: React.Key[];
  onSelectionChange: (keys: React.Key[]) => void;
  onPageChange: (page: number) => void;
  onPayNow: () => void;
  onDownloadStatement: () => void;
}

const FinancialsHistoryView: React.FC<FinancialsHistoryViewProps> = ({
  billingAccountId,
  rows,
  totalRowCount,
  emptyMessage,
  currentPage,
  totalPages,
  selectedRowKeys,
  onSelectionChange,
  onPageChange,
  onPayNow,
  onDownloadStatement,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [downloadingRowKey, setDownloadingRowKey] = useState<string | null>(
    null
  );

  const expandableKeys = useMemo(
    () =>
      new Set(
        rows
          .filter(
            (r) => r.kind === 'invoice' && r.data.appliedTransactions.length > 0
          )
          .map((r) => r.key)
      ),
    [rows]
  );

  const expandAll = () => {
    setExpandedRows(new Set(expandableKeys));
  };

  const collapseAll = () => {
    setExpandedRows(new Set());
  };

  const allExpanded =
    expandableKeys.size > 0 &&
    [...expandableKeys].every((k) => expandedRows.has(k));

  if (rows.length === 0) {
    return (
      <Flex
        justify="center"
        align="center"
        style={{ minHeight: 280, padding: 24 }}
      >
        <Text
          style={{
            fontSize: 14,
            color: loftColors.grays[75],
            textAlign: 'center',
            maxWidth: 420,
          }}
        >
          {emptyMessage}
        </Text>
      </Flex>
    );
  }

  const historyColumns: ColumnsType<HistoryTableRow> = [
    {
      title: 'Number',
      key: 'recordNo',
      width: '18%',
      render: (_, row) => {
        if (row.kind === 'appliedPayment') {
          return (
            <Text strong style={{ fontSize: CELL_FS }}>
              {String(row.data.recordNo)}
            </Text>
          );
        }
        if (row.kind === 'invoice') {
          const docNo = row.data.invoice.docNo;
          return (
            <Text strong style={{ fontSize: CELL_FS }}>
              {docNo != null && String(docNo).trim() !== ''
                ? String(docNo)
                : '—'}
            </Text>
          );
        }
        if (row.kind === 'creditMemo') {
          const cm = row.data as IntacctCreditMemo;
          return (
            <Flex align="center" gap={6}>
              <FileTextOutlined
                style={{ color: loftColors.grays[75], fontSize: CELL_FS }}
              />
              <Text strong style={{ fontSize: CELL_FS }}>
                {cm.docNo || cm.recordNo}
              </Text>
            </Flex>
          );
        }
        return (
          <Text strong style={{ fontSize: CELL_FS }}>
            {String(row.data.recordNo)}
          </Text>
        );
      },
    },
    {
      title: 'Date',
      key: 'date',
      width: '15%',
      render: (_, row) => {
        let dateStr: string;
        if (row.kind === 'invoice') {
          dateStr = formatDateMMDDYYYY(row.data.invoice.whenCreated);
        } else if (row.kind === 'creditMemo') {
          dateStr = formatDateMMDDYYYY(
            (row.data as IntacctCreditMemo).whenCreated
          );
        } else {
          dateStr = formatDateMMDDYYYY(row.data.whenCreated);
        }
        return <Text style={{ fontSize: CELL_FS }}>{dateStr}</Text>;
      },
    },
    {
      title: 'Due Date',
      key: 'dueDate',
      width: '15%',
      render: (_, row) => {
        if (row.kind === 'invoice') {
          return (
            <Text style={{ fontSize: CELL_FS }}>
              {formatDateMMDDYYYY(row.data.invoice.whenDue)}
            </Text>
          );
        }
        return (
          <Text style={{ fontSize: CELL_FS, color: loftColors.grays[50] }}>
            —
          </Text>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: '14%',
      render: (_, row) => {
        if (row.kind === 'invoice') {
          return (
            <HistoryStatusBadge
              row={row}
              status={getEffectiveStatus(row.data.invoice)}
            />
          );
        }
        if (row.kind === 'creditMemo') {
          return (
            <HistoryStatusBadge
              row={row}
              status={(row.data as IntacctCreditMemo).state}
            />
          );
        }
        if (row.kind === 'appliedPayment') {
          return <HistoryStatusBadge row={row} status="Applied" />;
        }
        return <HistoryStatusBadge row={row} status="Unapplied" />;
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      width: '16%',
      align: 'right',
      render: (_, row) => {
        if (row.kind === 'invoice') {
          return (
            <Text style={{ fontSize: CELL_FS }}>
              {formatCurrency(
                row.data.invoice.totalEntered,
                row.data.invoice.currency
              )}
            </Text>
          );
        }
        if (row.kind === 'creditMemo') {
          const cm = row.data as IntacctCreditMemo;
          const amount = safeParseAmount(cm.totalEntered);
          const absAmount = Math.abs(amount).toFixed(2);
          return (
            <Text style={{ fontSize: CELL_FS, color: loftColors.greens[100] }}>
              -{formatCurrency(absAmount, cm.currency || 'USD')}
            </Text>
          );
        }
        return (
          <Text style={{ fontSize: CELL_FS }}>
            -{formatCurrency(row.data.amountApplied, row.data.currency)}
          </Text>
        );
      },
    },
    {
      title: 'Balance',
      key: 'balance',
      width: '16%',
      align: 'right',
      render: (_, row) => {
        if (row.kind === 'invoice') {
          const balance = row.data.remainingBalance;
          const prefix = balance < 0 ? '-' : '';
          const display = `${prefix}${formatCurrency(
            Math.abs(balance).toFixed(2),
            row.data.invoice.currency
          )}`;
          return (
            <Text strong style={{ fontSize: CELL_FS }}>
              {display}
            </Text>
          );
        }
        if (row.kind === 'creditMemo') {
          const cm = row.data as IntacctCreditMemo;
          const totalDue = safeParseAmount(cm.totalDue);
          const absValue = Math.abs(totalDue).toFixed(2);
          const prefix = totalDue < 0 ? '-' : '';
          return (
            <Text strong style={{ fontSize: CELL_FS }}>
              {prefix}
              {formatCurrency(absValue, cm.currency || 'USD')}
            </Text>
          );
        }
        if (row.kind === 'appliedPayment') {
          return (
            <Text style={{ fontSize: CELL_FS, color: loftColors.grays[50] }}>
              —
            </Text>
          );
        }
        return (
          <Text strong style={{ fontSize: CELL_FS }}>
            -{formatCurrency(row.data.amountApplied, row.data.currency)}
          </Text>
        );
      },
    },
    {
      title: '',
      key: 'invoicePdf',
      width: 52,
      align: 'center',
      render: (_, row) => {
        if (row.kind !== 'invoice') {
          return null;
        }
        const inv = row.data.invoice;
        const loading = downloadingRowKey === row.key;
        return (
          <Tooltip title="Download invoice PDF">
            <Button
              type="link"
              size="small"
              aria-label="Download invoice PDF"
              icon={
                loading ? (
                  <LoadingOutlined />
                ) : (
                  <DownloadOutlined style={{ fontSize: 14 }} />
                )
              }
              disabled={loading}
              onClick={(e) => {
                e.stopPropagation();
                void (async () => {
                  setDownloadingRowKey(row.key);
                  try {
                    await handleDownloadInvoice(
                      {
                        recordNo: inv.recordNo,
                        docNo: inv.docNo ?? undefined,
                        whenCreated: inv.whenCreated,
                      },
                      billingAccountId
                    );
                  } catch (err) {
                    message.error(
                      err instanceof Error
                        ? err.message
                        : 'Failed to download invoice PDF'
                    );
                  } finally {
                    setDownloadingRowKey(null);
                  }
                })();
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Flex justify="flex-end" align="center" style={{ marginBottom: 12 }}>
        <Flex gap={8}>
          <Button
            type="primary"
            onClick={() => {
              if (window.analytics) {
                window.analytics.track('Pay Now Clicked', {
                  object_id: 'pay-now-button',
                  object_name: 'Pay Now',
                  object_location: 'Financials History',
                  order_id: getInvoiceDisplayIdsForPayNowTracking(
                    rows,
                    selectedRowKeys
                  ),
                });
              }
              onPayNow();
            }}
            size="small"
            icon={<DollarOutlined />}
            disabled={selectedRowKeys.length === 0}
          >
            Pay Now ({selectedRowKeys.length})
          </Button>
          <Button
            type="link"
            onClick={allExpanded ? collapseAll : expandAll}
            size="small"
            icon={allExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
          >
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={onDownloadStatement}
          >
            Download Statement
          </Button>
        </Flex>
      </Flex>

      <Table<HistoryTableRow>
        columns={historyColumns}
        dataSource={rows}
        rowKey="key"
        pagination={false}
        size="middle"
        tableLayout="fixed"
        rowSelection={{
          checkStrictly: true,
          selectedRowKeys,
          onChange: (newSelectedKeys) => {
            onSelectionChange(newSelectedKeys as React.Key[]);
          },
          getCheckboxProps: (row: HistoryTableRow) => {
            if (row.kind === 'appliedPayment') {
              return { disabled: true };
            }
            if (row.kind === 'credit' || row.kind === 'creditMemo') {
              return { disabled: true };
            }
            const grouped = row.data as GroupedHistory;
            const isPaid = grouped.invoice.state.toLowerCase() === 'paid';
            const isProcessing = !!grouped.invoice.paymentProcessingStatus;
            const noBalance = grouped.remainingBalance <= 0;
            return { disabled: isPaid || isProcessing || noBalance };
          },
          renderCell: (checked, record, index, originNode) => {
            if (record.kind === 'appliedPayment') {
              return (
                <Tooltip title="Only invoices can be selected for payment">
                  <span>{originNode}</span>
                </Tooltip>
              );
            }
            const isNonInvoice =
              record.kind === 'credit' || record.kind === 'creditMemo';
            const isPaid =
              !isNonInvoice &&
              record.data.invoice.state.toLowerCase() === 'paid';
            const isProcessing =
              !isNonInvoice && !!record.data.invoice.paymentProcessingStatus;
            const noBalance =
              !isNonInvoice &&
              (record.data as GroupedHistory).remainingBalance <= 0;
            const isDisabled =
              isNonInvoice || isPaid || isProcessing || noBalance;
            if (!isDisabled) return originNode;
            let tooltip = 'This invoice is already fully paid';
            if (isNonInvoice)
              tooltip = 'Only open invoices can be selected for payment';
            else if (isProcessing)
              tooltip = 'Payment is being processed for this invoice';
            return (
              <Tooltip title={tooltip}>
                <span>{originNode}</span>
              </Tooltip>
            );
          },
        }}
        expandable={{
          expandedRowKeys: Array.from(expandedRows),
          childrenColumnName: 'children',
          onExpand: (expanded, record) => {
            const key = String(record.key);
            setExpandedRows((prev) => {
              const next = new Set(prev);
              if (expanded) next.add(key);
              else next.delete(key);
              return next;
            });
          },
          rowExpandable: (record) =>
            record.kind === 'invoice' &&
            Array.isArray(record.children) &&
            record.children.length > 0,
          indentSize: 24,
        }}
      />

      {totalPages > 1 && (
        <Flex
          justify="space-between"
          align="center"
          style={{ marginTop: 16, padding: '8px 0' }}
        >
          <Text style={{ fontSize: CELL_FS, color: loftColors.grays[75] }}>
            Page {currentPage} of {totalPages}
          </Text>
          <Pagination
            current={currentPage}
            total={totalRowCount}
            pageSize={ROWS_PER_PAGE}
            onChange={onPageChange}
            showSizeChanger={false}
            size="small"
          />
        </Flex>
      )}
    </>
  );
};

export default FinancialsHistoryView;
